<script lang="ts">
	/**
	 * One dimension's rows (Design §13) — where the money came from, sliced.
	 *
	 * A control rather than a panel per dimension, for the reason the analytics breakdown is one:
	 * eleven tables of eleven dimensions is a page nobody scrolls to the bottom of, and the question
	 * is almost always "this one" asked one dimension at a time. Which dimensions a given instance
	 * offers is a prop, so the page can group them by the question they answer — a group heading is
	 * half the label, exactly as it is on the filter bar.
	 *
	 * **Every row is one (value, currency) pair**, never a value with its currencies merged. A
	 * product sold in euros and dollars is two rows here, and that is the honest rendering: the
	 * alternative is one row whose figure is not money in any currency.
	 *
	 * The bar is drawn against the **largest magnitude in the panel**, not against the total, so a
	 * refunds-only view still has bars — a dimension whose every value is negative would otherwise
	 * render as a table of empty tracks.
	 */

	import { Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel, SelectField } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import { getReportBreakdown } from '../../../AppNetwork';
	import type { ReportBreakdown, ReportDimension, ReportQuery } from '../../../AppTypes';

	export let filter: ReportQuery;
	export let title: string;
	export let faIcon: string;
	export let dimensions: { value: ReportDimension; name: string }[];
	/** Rendered under the title, where the split needs a sentence to be read correctly. */
	export let help: string = '';

	let dimension: ReportDimension = dimensions[0].value;
	let data: ReportBreakdown | null = null;
	let error = '';
	let loading = false;

	/** Keyed on a string, for the reason `TrendPanel` documents. */
	$: queryKey = JSON.stringify({ filter, dimension });
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			data = await getReportBreakdown(filter, dimension);
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the breakdown';
			data = null;
		} finally {
			loading = false;
		}
	}

	$: widest = Math.max(1, ...(data?.rows ?? []).map(row => Math.abs(row.net)));

	function fnMoney(amount: number, currency: string): string {
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}

	/**
	 * What to call a row whose grouping value is null.
	 *
	 * "Not recorded" rather than "Unknown", and never omitted: a movement with no product behind it
	 * is real money — a provider-side invoice with no local order, a chargeback against a charge
	 * nobody matched — and dropping those rows would make the panel's figures disagree with the
	 * summary above it with nothing on screen to explain the gap.
	 */
	function fnLabel(label: string | null, key: string | null): string {
		if (label !== null && label !== '') return label;
		if (key !== null && key !== '') return key;
		return 'Not recorded';
	}
</script>

<Panel {title} {faIcon}>
	<svelte:fragment slot="title">
		{#if help.length > 0}
			<span class="text-xs text-default">{help}</span>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="titleRight">
		<label class="text-xs text-default shrink-0" for={`reports-breakdown-${title}`}>Group by</label>
		<SelectField
			id={`reports-breakdown-${title}`}
			size="sm"
			class="w-44"
			bind:value={dimension}
			items={dimensions}
		/>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="The breakdown could not be loaded." description={error} />
	{:else if data === null}
		<EmptyState variant="loading" />
	{:else if data.rows.length === 0}
		<EmptyState variant="empty" title="Nothing to break down in this range." />
	{:else}
		<div class="overflow-x-auto" class:opacity-50={loading}>
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>Value</TableHeadCell>
					<TableHeadCell>Currency</TableHeadCell>
					<TableHeadCell class="text-right">Money in</TableHeadCell>
					<TableHeadCell class="text-right">Refunded</TableHeadCell>
					<TableHeadCell class="text-right">Net</TableHeadCell>
					<TableHeadCell class="text-right">Movements</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each data.rows as row (`${row.key ?? 'null'}:${row.currency}`)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<div>{fnLabel(row.label, row.key)}</div>
								<!-- The bar is decoration over a figure that is already stated, so it is
								     `aria-hidden`: a screen reader announcing a percentage of the largest
								     row is announcing something no column contains. -->
								<div class="mt-1 h-1 rounded bg-depth" aria-hidden="true">
									<div
										class="h-1 rounded bg-primary-500"
										style={`width: ${Math.round((Math.abs(row.net) / widest) * 100)}%`}
									/>
								</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable font-mono text-xs uppercase">
								{row.currency}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(row.gross, row.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{row.refunded + row.chargebacks === 0
									? '—'
									: fnMoney(-(row.refunded + row.chargebacks), row.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums font-medium">
								{fnMoney(row.net, row.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{row.movements.toLocaleString()}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>

		{#if data.remaining > 0}
			<!-- A truncated table says it is truncated. A top-twenty that looks like the whole set is
			     how a long tail gets reported as not existing. -->
			<p class="text-default text-xs mt-2">
				{data.remaining.toLocaleString()} further row{data.remaining === 1 ? '' : 's'} not shown.
				Narrow the filter, or use the export for the whole set.
			</p>
		{/if}
	{/if}
</Panel>
